import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import { validatePassword, validateForm } from '../../utils/validation';
import { resetPasswordApi } from '../../services/authService';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const domain = window.location.origin;

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [validationCriteria, setValidationCriteria] = useState({
    hasEightChar: false,
    hasLetter: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  const checkPasswordCriteria = (password) => {
    setValidationCriteria({
      hasEightChar: password.length >= 8,
      hasLetter: /[a-zA-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const validationRules = {
    password: validatePassword,
    confirmPassword: (value) => {
      if (!value) return ['Confirm password is required'];
      if (value.trim() !== formData.password.trim()) return ['Passwords do not match'];
      return [];
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
  
    if (name === 'password') {
      checkPasswordCriteria(value);
    }
  
    if (newFormData.password && newFormData.confirmPassword) {
      const password = newFormData.password.trim();
      const confirmPassword = newFormData.confirmPassword.trim();
  
      if (password === confirmPassword) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: ['Passwords do not match'] }));
      }
    }
  
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const password = formData.password.trim();
    const confirmPassword = formData.confirmPassword.trim();

    if (password !== confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: ['Passwords do not match'] }));
      return;
    }

    const validation = validateForm(formData, validationRules);
    if (validation.isValid) {
      setIsLoading(true);

      try {
        const payload = { email: email, newPassword: password, domain: domain };
        const response = await resetPasswordApi(payload);

        if (response.success) {
          toast.success('Password reset successfully!', {
            position: "top-center",
            autoClose: 2000,
            onClose: () => navigate('/auth/login')
          });
        } else {
          throw new Error(response.message || 'Failed to reset password');
        }
      } catch (error) {
        toast.error(error.message || 'Failed to reset password.', { position: "top-center", autoClose: 2000 });
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className="min-h-screen bg-light-background flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Logo */}
      <div className="mb-6">
        <img src={getLogoByDomainAndAlt().logo} alt={getLogoByDomainAndAlt().alt} className="h-10 w-auto" />
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-font-primary mb-2">Reset Password</h2>
          <p className="text-font-secondary text-sm">Enter your new password</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-font-primary mb-1.5">New Password</label>
            <div className="relative">
              <Icon icon="mdi:lock-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password" 
                className={`w-full pl-10 pr-12 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.password ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
              <button 
                type="button" 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                <Icon icon={showPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="w-5 h-5" />
              </button>
            </div>
            {errors.password && <span className="text-error text-xs mt-1 block">{errors.password[0]}</span>}
            
            {/* Password Requirements */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${validationCriteria.hasEightChar ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={validationCriteria.hasEightChar ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                8+ Characters
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${validationCriteria.hasLetter ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={validationCriteria.hasLetter ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                Letters
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${validationCriteria.hasNumber ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={validationCriteria.hasNumber ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                Numbers
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${validationCriteria.hasSpecialChar ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={validationCriteria.hasSpecialChar ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                Special Chars
              </span>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-font-primary mb-1.5">Confirm New Password</label>
            <div className="relative">
              <Icon icon="mdi:lock-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password" 
                className={`w-full pl-10 pr-12 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.confirmPassword ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
              <button 
                type="button" 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                <Icon icon={showConfirmPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="w-5 h-5" />
              </button>
            </div>
            {errors.confirmPassword && <span className="text-error text-xs mt-1 block">{errors.confirmPassword[0]}</span>}
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
          
          {/* Login Link */}
          <p className="text-center text-sm text-font-secondary">
            Remember your password?{' '}
            <Link 
              to="/auth/login" 
              className={`text-primary hover:text-primary/80 font-medium ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
            >
              Login here
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
